/** Which side of the content area Skipper is docked against. */
export type SkipperEdge = 'left' | 'right';

/** Where Skipper is sitting, and whether Skipper is tucked away. */
export interface SkipperPerch {
  edge: SkipperEdge;
  /**
   * Vertical position as a FRACTION of the usable height, not a pixel offset:
   * a pixel offset saved in a tall window strands Skipper off the bottom of a
   * short one. A fraction survives resizes.
   */
  verticalFraction: number;
  /** Pushed into the edge, mostly out of view, a wing peeking out. */
  tucked: boolean;
}

/**
 * Tucked away, low on the left. Skipper is a companion people opt into, not a
 * thing standing on their chart the first time they open the app.
 */
export const INITIAL_PERCH: SkipperPerch = { edge: 'left', verticalFraction: 0.82, tucked: true };

/** The companion's fixed metrics, from `skipper_floating_companion.dart`. */
export const COMPANION = {
  /** Skipper's rendered height. */
  size: 64,
  /** Skipper's rendered width — the avatar's own 193/190 aspect. */
  width: (64 * 193) / 190,
  /** Gap from the edge when docked but not tucked. */
  inset: 10,
  /** Fraction of Skipper's width pushed past an edge that turns a release into a tuck. */
  tuckTriggerFraction: 0.12,
  tuckBarWidth: 15,
  tuckBarHeight: 58,
  /** The bar plus a transparent strip that exists only to be tappable. */
  tuckAssemblyWidth: 38,
  /** How much of Skipper's width hides past the edge when tucked. */
  tuckInsetFraction: 0.94,
  /** Pointer travel (px) before a press becomes a drag, so a drag never also taps. */
  dragSlop: 5,
} as const;

/**
 * The area Skipper may occupy, in viewport px. Excludes the desktop side nav,
 * the mobile bottom nav and the top safe-area inset — bands where Skipper
 * would sit ON app chrome rather than over content.
 */
export interface PerchBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface Point {
  x: number;
  y: number;
}

export function verticalRange(b: PerchBounds): { min: number; max: number } {
  const min = b.top + 8;
  const max = b.bottom - COMPANION.size - 8;
  return { min, max: max < min ? min : max };
}

/** Top-left of the companion's box when settled on `perch`. */
export function settledTopLeft(perch: SkipperPerch, b: PerchBounds): Point {
  const right = perch.edge === 'right';
  // Tucked, the assembly is flush with the edge — it reads as attached.
  const x = perch.tucked
    ? right
      ? b.right - COMPANION.tuckAssemblyWidth
      : b.left
    : right
      ? b.right - COMPANION.width - COMPANION.inset
      : b.left + COMPANION.inset;
  const range = verticalRange(b);
  const fraction = Math.min(1, Math.max(0, perch.verticalFraction));
  return { x, y: range.min + (range.max - range.min) * fraction };
}

/**
 * Clamp applied DURING a drag. Loose horizontally — going past the edge is how
 * the user asks for the tuck — strict vertically.
 */
export function clampDrag(p: Point, b: PerchBounds): Point {
  const range = verticalRange(b);
  const minX = b.left - COMPANION.width * 0.8;
  const maxX = b.right - COMPANION.width * 0.2;
  return {
    x: Math.min(maxX, Math.max(minX, p.x)),
    y: Math.min(range.max, Math.max(range.min, p.y)),
  };
}

/**
 * Where a drag released at `released` (having started at `origin`, moving at
 * `velocity` px/s) comes to rest.
 *
 * A tuck needs intentional horizontal movement toward that edge — a push past
 * it, or a flick — so dragging Skipper up or down never tucks. Anything else
 * docks on the nearer edge, untucked, keeping the height.
 */
export function resolveRelease(
  released: Point,
  origin: Point,
  velocity: Point,
  b: PerchBounds,
): SkipperPerch {
  const w = COMPANION.width;
  const pastLeft = (b.left - released.x) / w;
  const pastRight = (released.x + w - b.right) / w;

  const range = verticalRange(b);
  const span = Math.abs(range.max - range.min) < 1 ? 1 : range.max - range.min;
  const verticalFraction = Math.min(1, Math.max(0, (released.y - range.min) / span));

  const horizontalTravel = released.x - origin.x;
  const movingLeft = horizontalTravel < -6;
  const movingRight = horizontalTravel > 6;
  const horizontalFlick = Math.abs(velocity.x) > Math.abs(velocity.y) && Math.abs(velocity.x) > 180;

  const flickLeft = movingLeft && horizontalFlick && velocity.x < -180;
  const flickRight = movingRight && horizontalFlick && velocity.x > 180;
  const pushedLeft =
    movingLeft && (pastLeft >= COMPANION.tuckTriggerFraction || released.x <= b.left + 4);
  const pushedRight =
    movingRight &&
    (pastRight >= COMPANION.tuckTriggerFraction || released.x + w >= b.right - 4);

  if (flickLeft || pushedLeft) return { edge: 'left', verticalFraction, tucked: true };
  if (flickRight || pushedRight) return { edge: 'right', verticalFraction, tucked: true };

  const centre = released.x + w / 2;
  return {
    edge: centre > (b.left + b.right) / 2 ? 'right' : 'left',
    verticalFraction,
    tucked: false,
  };
}

/** Validates a stored perch, clamping on READ so a bad value can never strand Skipper. */
export function parsePerch(raw: unknown): SkipperPerch | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Partial<Record<keyof SkipperPerch, unknown>>;
  const fraction = typeof value.verticalFraction === 'number' && isFinite(value.verticalFraction)
    ? Math.min(1, Math.max(0, value.verticalFraction))
    : INITIAL_PERCH.verticalFraction;
  return {
    edge: value.edge === 'right' ? 'right' : 'left',
    verticalFraction: fraction,
    tucked: typeof value.tucked === 'boolean' ? value.tucked : false,
  };
}
